const SUPABASE_URL = (typeof import.meta !== "undefined" && (import.meta as any).env?.VITE_SUPABASE_URL)
  || (typeof process !== "undefined" && process.env?.SUPABASE_URL)
  || "";

const STORAGE_BASE = `${SUPABASE_URL}/storage/v1/object/public/`;
const TRANSFORM_BASE = `${SUPABASE_URL}/storage/v1/render/image/public/`;

export interface ImageTransformOptions {
  width?: number;
  height?: number;
  quality?: number;
  format?: "webp" | "avif" | "origin";
  resize?: "cover" | "contain" | "fill";
}

/**
 * Returns a Supabase Image Transformation URL for images stored in Supabase Storage.
 * Falls back to the original URL for external images (Gravatar, QR codes, etc.).
 */
export function imgUrl(src: string | null | undefined, opts: ImageTransformOptions = {}): string | undefined {
  if (!src) return undefined;

  // Only transform Supabase Storage URLs
  if (!src.startsWith(STORAGE_BASE)) return src;

  const path = src.slice(STORAGE_BASE.length);
  const params = new URLSearchParams();

  if (opts.width) params.set("width", String(opts.width));
  if (opts.height) params.set("height", String(opts.height));
  if (opts.quality) params.set("quality", String(opts.quality));
  params.set("format", opts.format ?? "webp");
  if (opts.resize) params.set("resize", opts.resize);

  return `${TRANSFORM_BASE}${path}?${params.toString()}`;
}

/** Avatar thumbnail — 128px WebP */
export const avatarUrl = (src: string | null | undefined) =>
  imgUrl(src, { width: 128, height: 128, format: "webp", resize: "cover", quality: 80 });

/** Card thumbnail — 400px WebP */
export const cardUrl = (src: string | null | undefined) =>
  imgUrl(src, { width: 400, format: "webp", resize: "cover", quality: 80 });

/** Hero image — 1200px WebP */
export const heroUrl = (src: string | null | undefined) =>
  imgUrl(src, { width: 1200, format: "webp", quality: 85 });

/** Gallery image — 800px WebP */
export const galleryUrl = (src: string | null | undefined) =>
  imgUrl(src, { width: 800, format: "webp", quality: 82 });
